import { createFileRoute } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'
import { z } from 'zod'
import { SIGNAL_KINDS, type SignalKindFilter, signalKind, useFleetSignals } from '@/entities/fleet'
import { SelectField } from '@/shared/ui/field'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'
import { Spinner } from '@/shared/ui/spinner'

// Обе панели грузятся на переходе — стартовый бандл их не тянет (та же
// причина, что у lazyRouteComponent на карточках сущностей).
const SignalsBoard = lazy(() =>
  import('@/widgets/signals-board').then((m) => ({ default: m.SignalsBoard })),
)
const AutoscalerRun = lazy(() =>
  import('@/widgets/autoscaler-run').then((m) => ({ default: m.AutoscalerRun })),
)

function PanelFallback() {
  return (
    <div className="flex justify-center py-12 text-muted">
      <Spinner size={24} />
    </div>
  )
}

// Отбор живёт в URL: доску шлют ссылкой («глянь, что у нас с флотом»), а
// refresh не должен уводить обратно на «активные».
const searchSchema = z.object({
  scope: z.enum(['active', 'cleared']).catch('active'),
  kind: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/signals')({
  component: SignalsPage,
  validateSearch: searchSchema,
  staticData: { crumb: 'Требует внимания' },
})

// Экспорт нужен компонентному тесту: сам роут собирает его из этого же модуля.
export function SignalsPage() {
  const { scope, kind } = Route.useSearch()
  const navigate = Route.useNavigate()

  // Счётчики вкладок — по всей доске, а не по текущему отбору: «снятых нет»
  // должно быть видно до перехода.
  const all = useFleetSignals()
  const active = all.signals.filter((s) => s.active)
  const cleared = all.signals.filter((s) => !s.active)
  const kindFilter = kind && SIGNAL_KINDS.includes(kind) ? (kind as SignalKindFilter) : undefined

  return (
    <>
      <PageHeader
        title="Требует внимания"
        description="Сигналы флота и снимок последнего прогона автоскейлера — состояние системы без docker logs"
      />

      <div className="flex flex-col gap-8">
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="text-title font-medium">Сигналы</h2>
            <div className="flex flex-wrap items-end gap-3">
              <PillTabs
                aria-label="Отбор сигналов"
                className="h-11 items-center"
                value={scope}
                onChange={(value) =>
                  void navigate({ to: '/signals', search: { scope: value, kind } })
                }
                options={[
                  { value: 'active', label: 'Активные', count: active.length },
                  { value: 'cleared', label: 'Снятые', count: cleared.length },
                ]}
              />
              <SelectField
                label="Вид сигнала"
                className="w-64"
                value={kind ?? ''}
                onChange={(e) =>
                  void navigate({
                    to: '/signals',
                    search: { scope, ...(e.target.value ? { kind: e.target.value } : {}) },
                  })
                }
              >
                <option value="">Все виды</option>
                {SIGNAL_KINDS.map((code) => (
                  <option key={code} value={code}>
                    {signalKind(code).title}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>

          {scope === 'cleared' && (
            <p className="rounded-control bg-raise px-4 py-2.5 text-body-sm text-muted">
              История снятых: тревога была и ушла — это тоже ответ. Выводимый сигнал снял тик, когда
              условия не стало; одноразовый — оператор, разобравшись.
            </p>
          )}

          <Suspense fallback={<PanelFallback />}>
            <SignalsBoard
              active={scope === 'active'}
              {...(kindFilter ? { kind: kindFilter } : {})}
              emptyTitle={
                scope === 'active' ? 'Ничего не требует внимания' : 'Снятых сигналов ещё не было'
              }
              emptyDescription={
                scope === 'active'
                  ? 'Ни дефицита, ни просрочки, ни застрявших юнитов — флот идёт по заявкам'
                  : 'Ни одна тревога пока не уходила: ни сама, ни рукой оператора'
              }
            />
          </Suspense>
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-title font-medium">Последний прогон автоскейлера</h2>
            <p className="text-caption text-muted">
              Что система решила и почему — по каждой незакрытой заявке
            </p>
          </div>
          <Suspense fallback={<PanelFallback />}>
            <AutoscalerRun />
          </Suspense>
        </section>
      </div>
    </>
  )
}
